import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import Login from './components/Login'
import OAuthCallback from './components/OAuthCallback'
import Dashboard from './pages/Dashboard'
import HistoryPage from './pages/HistoryPage'
import OrangeSalonAnalysisPage from './pages/OrangeSalonAnalysisPage'
import OrangeDistrictAnalysisPage from './pages/OrangeDistrictAnalysisPage'
import OrangeFTEAnalysisPage from './pages/OrangeFTEAnalysisPage'
import OrangeVipStandardZeroPage from './pages/OrangeVipStandardZeroPage'
import OrangeOpaskaSalesPage from './pages/OrangeOpaskaSalesPage'
import OrangeLocatorSalesPage from './pages/OrangeLocatorSalesPage'
import OrangeLocatorVipStandardZeroPage from './pages/OrangeLocatorVipStandardZeroPage'
import OrangeLocatorDeviceSalesPage from './pages/OrangeLocatorDeviceSalesPage'
import PlusSalonAnalysisPage from './pages/PlusSalonAnalysisPage'
import PlusSalesPage from './pages/PlusSalesPage'
import PlusVipStandardZeroPage from './pages/PlusVipStandardZeroPage'
import PlaySalonAnalysisPage from './pages/PlaySalonAnalysisPage'
import TMobileSalonAnalysisPage from './pages/TMobileSalonAnalysisPage'
import SalesMapPage from './pages/SalesMapPage'
import AIAnalysisPage from './pages/AIAnalysisPage'
import AdminUsersPage from './pages/AdminUsersPage'
import GoalsManagementPage from './pages/GoalsManagementPage'
import AdminVisualization from './pages/AdminVisualization'
import DaysOffManagementPage from './pages/DaysOffManagementPage'
import AdminOperatorOrderPage from './pages/AdminOperatorOrderPage'
import ProductGroupsManagementPage from './pages/ProductGroupsManagementPage'
import MarginManagementPage from './pages/MarginManagementPage'
import AdminKamSyncPage from './pages/AdminKamSyncPage'
import AdminPosPage from './pages/AdminPosPage'
import TmobileAgentSalesPage from './pages/TmobileAgentSalesPage'
import PlusAgentSalesPage from './pages/PlusAgentSalesPage'
import OrangeAgentSalesPage from './pages/OrangeAgentSalesPage'
import OrangeDailySalesPage from './pages/OrangeDailySalesPage'
import OrangeShelfSalesPage from './pages/OrangeShelfSalesPage'
import OrangeLostOpportunitiesPage from './pages/OrangeLostOpportunitiesPage'
import OrangeRegionSummaryPage from './pages/OrangeRegionSummaryPage'
import PlusRegionRollupPage from './pages/PlusRegionRollupPage'
import AdminEmailSettingsPage from './pages/AdminEmailSettingsPage'
import McpSettingsPage from './pages/McpSettingsPage'
import McpConnectPage from './pages/McpConnectPage'
import { apiService } from './services/api'
import { UserProvider, useUser } from './context/UserContext'

function AdminOnly({ children }) {
  const { isAdmin } = useUser()
  return isAdmin ? children : <Navigate to="/dashboard" replace />
}

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authChecked, setAuthChecked] = useState(false)

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      const savedUser = localStorage.getItem('user')
      
      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await apiService.getCurrentUser()
          setUser(response.data)
        } catch (error) {
          console.error('Token validation failed:', error)
          // Token invalid, clear storage
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user')
          setUser(null)
        }
      }
      
      setLoading(false)
      setAuthChecked(true)
    }

    checkAuth()
  }, [])

  const handleLoginSuccess = (userData) => {
    setUser(userData)
  }

  const handleLogout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
    setUser(null)
  }

  // Show loading spinner while checking auth
  if (loading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Ładowanie...</p>
        </div>
      </div>
    )
  }

  // Show login if not authenticated
  if (!user) {
    return (
      <Router>
        <Routes>
          {/* OAuth callback route - accessible without authentication */}
          <Route path="/auth/callback" element={<OAuthCallback onLoginSuccess={handleLoginSuccess} />} />
          {/* Default login page */}
          <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      </Router>
    )
  }

  // Show app if authenticated
  return (
    <UserProvider user={user}>
      <Router>
        <Layout user={user} onLogout={handleLogout}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/:tab" element={<Dashboard />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/orange-salon-analysis" element={<OrangeSalonAnalysisPage />} />
            <Route path="/orange-districts" element={<OrangeDistrictAnalysisPage />} />
            <Route path="/orange-fte-analysis" element={<OrangeFTEAnalysisPage />} />
            <Route path="/orange-vip-standard-zero" element={<OrangeVipStandardZeroPage />} />
            <Route path="/orange-opaska-sales" element={<OrangeOpaskaSalesPage />} />
            <Route path="/orange-locator-sales" element={<OrangeLocatorSalesPage />} />
            <Route path="/orange-locator-vip-standard-zero" element={<OrangeLocatorVipStandardZeroPage />} />
            <Route path="/orange-locator-device-sales" element={<OrangeLocatorDeviceSalesPage />} />
            <Route path="/plus-salon-analysis" element={<PlusSalonAnalysisPage />} />
            <Route path="/plus-sales" element={<PlusSalesPage />} />
            <Route path="/plus-vip-standard-zero" element={<PlusVipStandardZeroPage />} />
            <Route path="/play-salon-analysis" element={<PlaySalonAnalysisPage />} />
            <Route path="/tmobile-salon-analysis" element={<TMobileSalonAnalysisPage />} />
            <Route path="/sales-map" element={<SalesMapPage />} />
            <Route path="/ai-analysis" element={<AIAnalysisPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/goals" element={<GoalsManagementPage />} />
            <Route path="/admin/visualization" element={<AdminVisualization />} />
            <Route path="/admin/operator-order" element={<AdminOperatorOrderPage />} />
            <Route path="/admin/days-off" element={<DaysOffManagementPage />} />
            <Route path="/admin/product-groups" element={<ProductGroupsManagementPage />} />
            <Route path="/admin/margins" element={<AdminOnly><MarginManagementPage /></AdminOnly>} />
            <Route path="/admin/kam-sync" element={<AdminOnly><AdminKamSyncPage /></AdminOnly>} />
            <Route path="/admin/pos" element={<AdminOnly><AdminPosPage /></AdminOnly>} />
            <Route path="/tmobile-agent-sales" element={<TmobileAgentSalesPage />} />
            <Route path="/plus-agent-sales" element={<PlusAgentSalesPage />} />
            <Route path="/orange-agent-sales" element={<OrangeAgentSalesPage />} />
            <Route path="/orange-daily-sales" element={<OrangeDailySalesPage />} />
            <Route path="/orange-shelf-sales" element={<OrangeShelfSalesPage />} />
            <Route path="/orange-lost-opportunities" element={<OrangeLostOpportunitiesPage />} />
            <Route path="/orange-region-summary" element={<OrangeRegionSummaryPage />} />
            <Route path="/plus-region-rollup" element={<PlusRegionRollupPage />} />
            <Route path="/admin/email" element={<AdminOnly><AdminEmailSettingsPage /></AdminOnly>} />
            <Route path="/settings/mcp" element={<McpSettingsPage />} />
            {/* Bridge route for MCP/OAuth: backend redirects here when
                mcp_session cookie is missing. Inits the cookie via
                /mcp/oauth/init-session and bounces back to authorize URL. */}
            <Route path="/mcp/connect" element={<McpConnectPage />} />
            {/* Catch-all: if a logged-in user lands on /login (e.g. after
                api.js interceptor blew localStorage on a 401 but before the
                hard reload completed) — bounce to dashboard instead of
                rendering a blank screen. */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
      </Router>
    </UserProvider>
  )
}

export default App
